/**
 * LeaderboardDrawer — anchor-themed XP leaderboard panel.
 *
 * Right-edge drawer matching the design-system theme:
 *
 *   1. Header — eyebrow + Big Shoulders caps title + close button
 *   2. User card — pinned, red-rail, current rank + XP + level
 *   3. Podium — 2nd / 1st / 3rd, 1st elevated, gold/silver/bronze accents
 *   4. Table — IBM Plex Mono rank numerics + name + level + XP
 *   5. Load-more — sharp-corner button that paginates 50 rows at a time
 *
 * Data: identical to the legacy purple drawer — useGetXPLeaderboard +
 * useGetUserProfile + useGetUserLeaderboardRank + useGetUserLevel. Sort
 * order and load semantics preserved (additive-only redesign).
 */

import React, {ReactElement, useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useQueryClient} from "@tanstack/react-query";
import classNames from "classnames";
import {Token, UserRankData} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import {
    decrementModalCount,
    incrementModalCount,
    toggleHideBottomNav,
} from "@/redux/meta/metaActions";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetUserLeaderboardRank} from "@/hooks/useGetUserLeaderboardRank";
import {useGetUserLevel} from "@/hooks/useGetUserLevel";
import {useGetXPLeaderboard} from "@/hooks/useGetXPLeaderboard";
import {formatNumberWithCommas} from "@/utils/formatNumberWithCommas";
import {getRankSuffix} from "@/utils/getRankSuffix";
import UserAvatar from "@/components/UserAvatar";
import {DEFAULT_AVATAR} from "@/utils/resolveUserAvatar";
import "./LeaderboardDrawer.scss";

interface Props {
    show: boolean;
    toggle: () => void;
}

const PAGE_SIZE = 50;

function podiumAccent(rank: number): string {
    if (rank === 1) return "lb-podium-cell--gold";
    if (rank === 2) return "lb-podium-cell--silver";
    if (rank === 3) return "lb-podium-cell--bronze";
    return "";
}

function LeaderboardDrawer(props: Props): ReactElement {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken) as Token | undefined;
    const {data: currentUser} = useGetUserProfile(fullToken!);
    const {data: leaderboardRankData} = useGetUserLeaderboardRank(fullToken!);
    const {data: accountLevel} = useGetUserLevel(fullToken!, currentUser?.user?.id ?? "");

    const [allUsers, setAllUsers] = useState<UserRankData[]>([]);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    const {data: leaderboardData, isLoading} = useGetXPLeaderboard(PAGE_SIZE, offset, props.show);

    useEffect(() => {
        if (!leaderboardData) return;
        if (offset === 0) {
            setAllUsers(leaderboardData.users);
        } else {
            setAllUsers(prev => [...prev, ...leaderboardData.users]);
        }
        setTotal(leaderboardData.total);
    }, [leaderboardData, offset]);

    useEffect(() => {
        if (props.show) {
            dispatch(incrementModalCount());
            dispatch(toggleHideBottomNav(true));
            setOffset(0);
            setAllUsers([]);
            queryClient.removeQueries({queryKey: ["xp-leaderboard"]});
            return () => {
                dispatch(decrementModalCount());
                dispatch(toggleHideBottomNav(false));
            };
        }
        dispatch(toggleHideBottomNav(false));
    }, [props.show]);

    function handleClose(e: React.MouseEvent | React.TouchEvent): void {
        e.stopPropagation();
        props.toggle();
    }

    const handleLoadMore = useCallback(() => {
        if (allUsers.length < total && !isLoading) {
            setOffset(prev => prev + PAGE_SIZE);
        }
    }, [allUsers.length, total, isLoading]);

    const userRank = leaderboardRankData?.rank;
    const userLevel = accountLevel?.level ?? currentUser?.user?.level ?? 1;
    const shouldShowRank = userLevel > 1;

    function renderUserCard(): ReactElement | null {
        if (!fullToken || !currentUser?.user) return null;
        return (
            <section className="lb-user" aria-label="Your standings">
                <div className="lb-user-rail" aria-hidden="true" />
                <UserAvatar
                    className="lb-user-avatar"
                    user={currentUser.user}
                    size="fill"
                    alt="Your profile"
                />
                <div className="lb-user-body">
                    <span className="lb-user-name">{currentUser.user.nickname || "GoVIP Player"}</span>
                    <span className="lb-user-rank">
                        {shouldShowRank && userRank
                            ? `Rank ${userRank}${getRankSuffix(userRank)}`
                            : "Unlock at Level 2"}
                    </span>
                </div>
                <div className="lb-user-stats">
                    <span className="lb-user-stat-value">
                        {formatNumberWithCommas(currentUser.user.experiencePoints ?? 0)}
                    </span>
                    <span className="lb-user-stat-label">XP</span>
                    <span className="lb-user-stat-divider" aria-hidden="true" />
                    <span className="lb-user-stat-value">{userLevel}</span>
                    <span className="lb-user-stat-label">LVL</span>
                </div>
            </section>
        );
    }

    function renderPodium(users: UserRankData[]): ReactElement | null {
        const top3 = users.slice(0, 3);
        if (top3.length < 3) return null;
        const podiumOrder = [top3[1], top3[0], top3[2]];
        const positions: Array<"second" | "first" | "third"> = ["second", "first", "third"];

        return (
            <section className="lb-podium" aria-label="Top 3 players">
                {podiumOrder.map((user, i) =>
                    <div
                        key={`podium-${user.rank}`}
                        className={classNames(
                            "lb-podium-cell",
                            `lb-podium-cell--${positions[i]}`,
                            podiumAccent(user.rank),
                        )}
                    >
                        <span className="lb-podium-rank">#{user.rank}</span>
                        <img
                            className="lb-podium-avatar"
                            src={user.pfpUrl || DEFAULT_AVATAR}
                            alt={user.nickname}
                        />
                        <span className="lb-podium-name">{user.nickname}</span>
                        <span className="lb-podium-xp">{formatNumberWithCommas(user.experiencePoints)} XP</span>
                        <span className="lb-podium-level">LVL {user.level}</span>
                    </div>)}
            </section>
        );
    }

    function renderTable(users: UserRankData[]): ReactElement | null {
        const remaining = users.slice(3);
        if (remaining.length === 0) return null;
        return (
            <section className="lb-table" aria-label="Leaderboard rows 4 and below">
                <header className="lb-table-head">
                    <span className="lb-table-h-rank">Rank</span>
                    <span className="lb-table-h-player">Player</span>
                    <span className="lb-table-h-level">LVL</span>
                    <span className="lb-table-h-xp">XP</span>
                </header>
                <ul className="lb-table-body">
                    {remaining.map(user =>
                        <li
                            key={`row-${user.rank}`}
                            className={classNames("lb-row", {"lb-row--me": userRank === user.rank})}
                        >
                            <span className="lb-row-rank">{user.rank}</span>
                            <div className="lb-row-player">
                                <img
                                    className="lb-row-avatar"
                                    src={user.pfpUrl || DEFAULT_AVATAR}
                                    alt={user.nickname}
                                />
                                <span className="lb-row-name">{user.nickname}</span>
                            </div>
                            <span className="lb-row-level">{user.level}</span>
                            <span className="lb-row-xp">{formatNumberWithCommas(user.experiencePoints)}</span>
                        </li>)}
                </ul>
            </section>
        );
    }

    return (
        <>
            <div
                className={classNames("leaderboard-drawer__overlay", {
                    "leaderboard-drawer__overlay--visible": props.show,
                })}
                onClick={handleClose}
                role="presentation"
            />
            <aside
                className={classNames("leaderboard-drawer", {"leaderboard-drawer--open": props.show})}
                role="dialog"
                aria-label="XP Leaderboard"
                aria-hidden={!props.show}
            >
                <header className="lb-hd">
                    <div className="lb-hd-row">
                        <div className="lb-hd-eyebrow">GLOBAL STANDINGS</div>
                        <button
                            type="button"
                            className="lb-hd-close"
                            onClick={handleClose}
                            aria-label="Close leaderboard drawer"
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <h2 className="lb-hd-title">XP <span>LEADERBOARD</span></h2>
                    <p className="lb-hd-sub">Top players by experience earned across the platform.</p>
                </header>

                {renderUserCard()}

                <div className="lb-content">
                    {isLoading && allUsers.length === 0 &&
                        <div className="lb-loading" role="status" aria-busy="true">
                            <span className="lb-loading-dot lb-loading-dot--1" />
                            <span className="lb-loading-dot lb-loading-dot--2" />
                            <span className="lb-loading-dot lb-loading-dot--3" />
                        </div>
                    }

                    {!(isLoading && allUsers.length === 0) &&
                        <>
                            {renderPodium(allUsers)}
                            {renderTable(allUsers)}
                            {allUsers.length < total &&
                                <button
                                    type="button"
                                    className="lb-load-more"
                                    onClick={handleLoadMore}
                                    disabled={isLoading}
                                >
                                    {isLoading ? "LOADING…" : "LOAD MORE"}
                                </button>
                            }
                        </>
                    }
                </div>
            </aside>
        </>
    );
}

export default LeaderboardDrawer;
